import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  customer_id: "", business_name: "", contact_person: "", mobile: "", whatsapp: "", email: "",
  plan_id: "", plan_name: "", start_date: "", end_date: "", assigned_staff_id: "",
  customer_requirements: "", internal_notes: "", total_amount: "", paid_amount: "",
  payment_method: "", target_leads: "",
};

function addDays(dateStr, days) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days - 1);
  return d.toISOString().slice(0, 10);
}

export function OrderFormDialog({ open, onOpenChange, onSaved, editOrder = null }) {
  const isEdit = !!editOrder;
  const [f, setF] = useState(empty);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [staff, setStaff] = useState([]);
  const [mode, setMode] = useState("existing"); // existing | new
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setF({
        customer_id: editOrder.customer_id || "",
        business_name: editOrder.business_name || "",
        contact_person: editOrder.contact_person || "",
        mobile: editOrder.mobile || "",
        whatsapp: editOrder.whatsapp || "",
        email: editOrder.email || "",
        plan_id: editOrder.plan_id || "",
        plan_name: editOrder.plan_name || "",
        start_date: editOrder.start_date || "",
        end_date: editOrder.end_date || "",
        assigned_staff_id: editOrder.assigned_staff_id || "",
        customer_requirements: editOrder.customer_requirements || "",
        internal_notes: editOrder.internal_notes || "",
        total_amount: editOrder.total_amount ?? "",
        paid_amount: editOrder.paid_amount ?? "",
        payment_method: editOrder.payment_method || "",
        target_leads: editOrder.target_leads ?? "",
      });
      setMode("existing");
    } else {
      setF(empty); setMode("existing");
      api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    }
    api.get("/plans", { params: { active_only: true } }).then((r) => setPlans(r.data)).catch(() => {});
    api.get("/users", { params: { role: "staff" } }).then((r) => setStaff(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    if (c) setF((p) => ({ ...p, customer_id: id, business_name: c.business_name, contact_person: c.contact_person, mobile: c.mobile, whatsapp: c.whatsapp, email: c.email }));
  };

  const pickPlan = (id) => {
    const p = plans.find((x) => x.id === id);
    setF((prev) => {
      const next = { ...prev, plan_id: id, plan_name: p?.name || "", total_amount: p?.price ?? prev.total_amount, target_leads: p?.target_leads || prev.target_leads };
      if (p?.duration_days && prev.start_date) next.end_date = addDays(prev.start_date, p.duration_days);
      return next;
    });
  };

  const onStart = (v) => {
    const p = plans.find((x) => x.id === f.plan_id);
    setF((prev) => ({ ...prev, start_date: v, end_date: p?.duration_days && v ? addDays(v, p.duration_days) : prev.end_date }));
  };

  const submit = async () => {
    if (!f.business_name) { toast.error("Business name is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          business_name: f.business_name, contact_person: f.contact_person,
          mobile: f.mobile, whatsapp: f.whatsapp, email: f.email,
          plan_id: f.plan_id || null, plan_name: f.plan_name,
          start_date: f.start_date, end_date: f.end_date,
          assigned_staff_id: f.assigned_staff_id || null,
          customer_requirements: f.customer_requirements, internal_notes: f.internal_notes,
          total_amount: Number(f.total_amount || 0), target_leads: Number(f.target_leads || 0),
        };
        await api.put(`/orders/${editOrder.order_id}`, payload);
        toast.success(`Order ${editOrder.order_id} updated`);
      } else {
        const payload = {
          ...f,
          total_amount: Number(f.total_amount || 0),
          paid_amount: Number(f.paid_amount || 0),
          target_leads: Number(f.target_leads || 0),
          customer_id: mode === "existing" ? (f.customer_id || null) : null,
        };
        const { data } = await api.post("/orders", payload);
        toast.success(`Order ${data.order_id} created`);
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? `Edit Order ${editOrder.order_id}` : "Create New Order"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {!isEdit && (
          <section className="space-y-3">
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode("existing")} data-testid="mode-existing"
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${mode === "existing" ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600"}`}>Existing Customer</button>
              <button type="button" onClick={() => setMode("new")} data-testid="mode-new"
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${mode === "new" ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600"}`}>New Customer</button>
            </div>
            {mode === "existing" && (
              <div>
                <Label>Select Customer</Label>
                <Select value={f.customer_id} onValueChange={pickCustomer}>
                  <SelectTrigger className="mt-1.5" data-testid="select-customer"><SelectValue placeholder="Choose a customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No customers yet — add one under Customers.</div>}
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.business_name} — {c.contact_person}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </section>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Business Name *"><Input data-testid="of-business" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} /></Field>
            <Field label="Contact Person"><Input data-testid="of-contact" value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></Field>
            <Field label="Mobile"><Input data-testid="of-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /></Field>
            <Field label="WhatsApp"><Input data-testid="of-whatsapp" value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
            <Field label="Email"><Input data-testid="of-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Plan / Service">
              <Select value={f.plan_id} onValueChange={pickPlan}>
                <SelectTrigger className="mt-1.5" data-testid="select-plan"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                <SelectContent>
                  {plans.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No active plans — add under Plans.</div>}
                  {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Start Date"><Input data-testid="of-start" type="date" value={f.start_date} onChange={(e) => onStart(e.target.value)} /></Field>
            <Field label="End Date"><Input data-testid="of-end" type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></Field>
            <Field label="Assigned Staff">
              <Select value={f.assigned_staff_id} onValueChange={(v) => set("assigned_staff_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="select-staff"><SelectValue placeholder="Assign staff" /></SelectTrigger>
                <SelectContent>
                  {staff.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No staff yet — add under Settings.</div>}
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Target Leads (optional)"><Input data-testid="of-target" type="number" value={f.target_leads} onChange={(e) => set("target_leads", e.target.value)} /></Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Total Amount (₹)"><Input data-testid="of-total" type="number" value={f.total_amount} onChange={(e) => set("total_amount", e.target.value)} /></Field>
            {!isEdit && <Field label="Paid Amount (₹)"><Input data-testid="of-paid" type="number" value={f.paid_amount} onChange={(e) => set("paid_amount", e.target.value)} /></Field>}
            <Field label="Payment Method">
              <Select value={f.payment_method} onValueChange={(v) => set("payment_method", v)}>
                <SelectTrigger className="mt-1.5" data-testid="select-method"><SelectValue placeholder="Method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Customer Requirements"><Textarea data-testid="of-req" rows={2} value={f.customer_requirements} onChange={(e) => set("customer_requirements", e.target.value)} /></Field>
          <Field label="Internal Notes (staff/admin only)"><Textarea data-testid="of-notes" rows={2} value={f.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="submit-order" onClick={submit} disabled={saving} className="bg-[#1e3a8a] hover:bg-[#1e40af]">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {isEdit ? "Save Changes" : "Create Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
